import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { useGetProjectWorkflowDetailQuery } from '../../store/api/projectManagementApi';
import {
  Layout,
  Card,
  Steps,
  Typography,
  Space,
  Button,
  Progress,
  Badge,
  Tag,
  Alert,
  Tooltip,
  List,
  Row,
  Col,
  Statistic,
  Timeline,
  Divider,
  message,
  Avatar,
  Breadcrumb,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  SaveOutlined,
  SendOutlined,
  FileTextOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  SafetyOutlined,
  SettingOutlined,
  AuditOutlined,
  TrophyOutlined,
  RightOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
} from '@ant-design/icons';

// Import task components
import TaskContent from './workflow-tasks/TaskContent';

// Emerald green color system
const EMERALD_COLORS = {
  primary: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981', // main color
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
  },
  semantic: {
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    pending: '#94a3b8',
    completed: '#10b981',
    inProgress: '#f59e0b',
  },
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

const { Content, Sider } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

// Mapping from task id to i18n key path (stage.taskKey)
const TASK_I18N_MAP: Record<string, { stage: string; taskKey: string }> = {
  ai_assessment: { stage: 'questionnaire', taskKey: 'aiAssessment' },
  compliance_check: { stage: 'questionnaire', taskKey: 'complianceCheck' },
  market_access: { stage: 'questionnaire', taskKey: 'marketAccess' },
  issuer_analysis: { stage: 'detailedAssessment', taskKey: 'issuerAnalysis' },
  financial_analysis: {
    stage: 'detailedAssessment',
    taskKey: 'financialAnalysis',
  },
  green_project: { stage: 'detailedAssessment', taskKey: 'greenProject' },
  issuance_structure: {
    stage: 'detailedAssessment',
    taskKey: 'issuanceStructure',
  },
  service_providers: {
    stage: 'detailedAssessment',
    taskKey: 'serviceProviders',
  },
  risk_management: { stage: 'detailedAssessment', taskKey: 'riskManagement' },
  legal_dd: { stage: 'dueDiligence', taskKey: 'legalDD' },
  financial_dd: { stage: 'dueDiligence', taskKey: 'financialDD' },
  business_dd: { stage: 'dueDiligence', taskKey: 'businessDD' },
  esg_dd: { stage: 'dueDiligence', taskKey: 'esgDD' },
  green_certification: { stage: 'dueDiligence', taskKey: 'greenCertification' },
  spo_process: { stage: 'dueDiligence', taskKey: 'spoProcess' },
  internal_approval: { stage: 'projectApproval', taskKey: 'internalApproval' },
  regulatory_filing: { stage: 'projectApproval', taskKey: 'regulatoryFiling' },
  resource_allocation: {
    stage: 'projectApproval',
    taskKey: 'resourceAllocation',
  },
  legal_documentation: {
    stage: 'issuancePreparation',
    taskKey: 'legalDocumentation',
  },
  rating_application: {
    stage: 'issuancePreparation',
    taskKey: 'ratingApplication',
  },
  roadshow_preparation: {
    stage: 'issuancePreparation',
    taskKey: 'roadshowPreparation',
  },
  system_setup: { stage: 'issuancePreparation', taskKey: 'systemSetup' },
  marketing: { stage: 'marketIssuance', taskKey: 'marketing' },
  book_building: { stage: 'marketIssuance', taskKey: 'bookBuilding' },
  issuance_execution: { stage: 'marketIssuance', taskKey: 'issuanceExecution' },
  listing_trading: { stage: 'marketIssuance', taskKey: 'listingTrading' },
};

// Mapping from workflow config step key to i18n stage key
const STAGE_I18N_MAP: Record<string, string> = {
  questionnaire: 'questionnaire',
  detailed_assessment: 'detailedAssessment',
  due_diligence: 'dueDiligence',
  project_approval: 'projectApproval',
  issuance_preparation: 'issuancePreparation',
  market_issuance: 'marketIssuance',
};

// Global workflow config - stores only structural data, no Chinese text
// All display strings are resolved at render time via i18n
const GLOBAL_WORKFLOW_CONFIG = {
  questionnaire: {
    stageKey: 'questionnaire',
    icon: <FileTextOutlined />,
    status: 'completed',
    progress: 100,
    estimatedDays: 5,
    tasks: [
      {
        id: 'ai_assessment',
        status: 'completed',
        progress: 100,
        estimatedMinutes: 30,
        fieldKeys: ['riskScoring', 'industryBenchmark', 'feasibilityJudgment'],
      },
      {
        id: 'compliance_check',
        status: 'completed',
        progress: 100,
        estimatedMinutes: 45,
        fieldKeys: [
          'greenStandard',
          'regulatoryCompliance',
          'basicQualification',
        ],
      },
      {
        id: 'market_access',
        status: 'completed',
        progress: 100,
        estimatedMinutes: 25,
        fieldKeys: ['issuanceQualification', 'projectScale', 'marketTiming'],
      },
    ],
  },
  detailed_assessment: {
    stageKey: 'detailedAssessment',
    icon: <AuditOutlined />,
    status: 'in_progress',
    progress: 45,
    estimatedDays: 15,
    tasks: [
      {
        id: 'issuer_analysis',
        status: 'completed',
        progress: 100,
        estimatedMinutes: 120,
        fieldKeys: [
          'governanceStructure',
          'managementTeam',
          'equityRelation',
          'competitiveAdvantage',
        ],
      },
      {
        id: 'financial_analysis',
        status: 'in_progress',
        progress: 60,
        estimatedMinutes: 150,
        fieldKeys: ['balanceSheet', 'cashFlow', 'financialRatios', 'solvency'],
      },
      {
        id: 'green_project',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 180,
        fieldKeys: [
          'greenBondFramework',
          'environmentalQuantification',
          'greenCertification',
          'projectFeasibility',
        ],
      },
      {
        id: 'issuance_structure',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 90,
        fieldKeys: [
          'issuanceScale',
          'maturityStructure',
          'interestDesign',
          'creditEnhancement',
        ],
      },
      {
        id: 'service_providers',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 60,
        fieldKeys: ['underwriter', 'ratingAgency', 'legalCounsel', 'auditor'],
      },
      {
        id: 'risk_management',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 75,
        fieldKeys: [
          'marketRisk',
          'creditRisk',
          'operationalRisk',
          'complianceRisk',
        ],
      },
    ],
  },
  due_diligence: {
    stageKey: 'dueDiligence',
    icon: <SafetyOutlined />,
    status: 'pending',
    progress: 0,
    estimatedDays: 20,
    tasks: [
      {
        id: 'legal_dd',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 240,
        fieldKeys: [
          'companyLegality',
          'materialContracts',
          'litigationRisk',
          'intellectualProperty',
        ],
      },
      {
        id: 'financial_dd',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 300,
        fieldKeys: [
          'financialAuthenticity',
          'accountingPolicy',
          'relatedPartyTransactions',
          'contingentLiabilities',
        ],
      },
      {
        id: 'business_dd',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 180,
        fieldKeys: [
          'businessModel',
          'marketPosition',
          'coreTechnology',
          'developmentStrategy',
        ],
      },
      {
        id: 'esg_dd',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 120,
        fieldKeys: [
          'environmentalManagement',
          'socialResponsibility',
          'corporateGovernance',
          'sustainability',
        ],
      },
      {
        id: 'green_certification',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 90,
        fieldKeys: [
          'thirdPartyCertification',
          'internationalStandards',
          'environmentalBenefit',
          'regulatoryRequirements',
        ],
      },
      {
        id: 'spo_process',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 960,
        fieldKeys: ['engagementAgreement', 'spoFramework', 'spReport'],
      },
    ],
  },
  project_approval: {
    stageKey: 'projectApproval',
    icon: <CheckCircleOutlined />,
    status: 'pending',
    progress: 0,
    estimatedDays: 7,
    tasks: [
      {
        id: 'internal_approval',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 120,
        fieldKeys: [
          'committeeResolution',
          'riskControlApproval',
          'complianceConfirmation',
          'seniorApproval',
        ],
      },
      {
        id: 'regulatory_filing',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 180,
        fieldKeys: [
          'regulatoryFiling',
          'issuanceApplication',
          'complianceDocuments',
          'approvalTracking',
        ],
      },
      {
        id: 'resource_allocation',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 90,
        fieldKeys: [
          'teamFormation',
          'budgetAllocation',
          'timelinePlanning',
          'kpiSetting',
        ],
      },
    ],
  },
  issuance_preparation: {
    stageKey: 'issuancePreparation',
    icon: <SettingOutlined />,
    status: 'pending',
    progress: 0,
    estimatedDays: 30,
    tasks: [
      {
        id: 'legal_documentation',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 360,
        fieldKeys: [
          'prospectus',
          'underwritingAgreement',
          'trustIndenture',
          'legalOpinion',
        ],
      },
      {
        id: 'rating_application',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 240,
        fieldKeys: [
          'agencySelection',
          'materialPreparation',
          'ratingMeeting',
          'reportIssuance',
        ],
      },
      {
        id: 'roadshow_preparation',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 180,
        fieldKeys: [
          'investorMaterials',
          'roadshowArrangement',
          'managementTraining',
          'marketStrategy',
        ],
      },
      {
        id: 'system_setup',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 120,
        fieldKeys: [
          'platformConfig',
          'accountManagement',
          'settlementArrangement',
          'systemTesting',
        ],
      },
    ],
  },
  market_issuance: {
    stageKey: 'marketIssuance',
    icon: <TrophyOutlined />,
    status: 'pending',
    progress: 0,
    estimatedDays: 10,
    tasks: [
      {
        id: 'marketing',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 300,
        fieldKeys: [
          'investorRoadshow',
          'mediaPromotion',
          'analystMeetings',
          'marketFeedback',
        ],
      },
      {
        id: 'book_building',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 180,
        fieldKeys: [
          'investorInquiries',
          'priceDiscovery',
          'allocationDecisions',
          'resultConfirmation',
        ],
      },
      {
        id: 'issuance_execution',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 120,
        fieldKeys: [
          'termsDetermination',
          'documentSigning',
          'capitalRaising',
          'bondDelivery',
        ],
      },
      {
        id: 'listing_trading',
        status: 'pending',
        progress: 0,
        estimatedMinutes: 90,
        fieldKeys: [
          'exchangeListing',
          'marketMaker',
          'liquiditySupport',
          'tradingMonitoring',
        ],
      },
    ],
  },
};

const ProjectDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { projectCode } = useParams<{ projectCode: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = useSelector((state: RootState) => state.auth.user?.role);

  // Helper to translate workflow-specific keys (supports interpolation options)
  const tw = (key: string, options?: Record<string, unknown>) =>
    t(`project:projectDetail.${key}`, options as any);

  // Helper to get translated stage title from stageKey
  const getStageName = (stageKey: string) =>
    tw(`workflow.stageTitles.${stageKey}`);

  // Helper to get translated stage description from stageKey
  const getStageDescription = (stageKey: string) =>
    tw(`workflow.stageDescriptions.${stageKey}`);

  // Helper to get translated task title/description
  const getTaskTitle = (taskId: string) => {
    const mapping = TASK_I18N_MAP[taskId];
    if (!mapping) return taskId;
    return tw(`workflow.${mapping.stage}.tasks.${mapping.taskKey}.title`);
  };

  const getTaskDescription = (taskId: string) => {
    const mapping = TASK_I18N_MAP[taskId];
    if (!mapping) return '';
    return tw(`workflow.${mapping.stage}.tasks.${mapping.taskKey}.description`);
  };

  // Helper to get translated field names for a task
  const getTaskFieldName = (taskId: string, fieldKey: string) => {
    const mapping = TASK_I18N_MAP[taskId];
    if (!mapping) return fieldKey;
    return tw(
      `workflow.${mapping.stage}.tasks.${mapping.taskKey}.fields.${fieldKey}`
    );
  };

  // Normalize backend placeholders to locale-specific labels
  const normalizeBondTerm = (bondTerm?: string | null) => {
    const rawTerm = (bondTerm || '').trim();
    if (!rawTerm) return tw('values.unknownTerm');

    const normalized = rawTerm.toLowerCase();
    if (
      ['未知期限', 'unknown term', 'unknown', 'tbd', '待确定', 'n/a'].includes(
        normalized
      )
    ) {
      return tw('values.unknownTerm');
    }

    const yearMatch =
      rawTerm.match(/^(\d+)\s*年$/) ||
      rawTerm.match(/^(\d+)\s*(years?|yrs?|y)$/i);
    if (yearMatch?.[1]) {
      return tw('values.termYears', { count: Number(yearMatch[1]) });
    }

    return rawTerm;
  };

  const normalizeProjectType = (projectType?: unknown) => {
    const rawType = Array.isArray(projectType)
      ? String(projectType[0] || '').trim()
      : String(projectType || '').trim();

    if (!rawType) return tw('values.unknownProjectType');

    const normalized = rawType.toLowerCase();
    if (
      ['未知类型', 'unknown type', 'unknown', 'tbd', '待确定', 'n/a'].includes(
        normalized
      )
    ) {
      return tw('values.unknownProjectType');
    }

    return rawType;
  };

  const getLocalizedCurrentStage = (stage?: string) => {
    if (!stage) return tw('values.unknownStage');
    const stageI18nKey = STAGE_I18N_MAP[stage];
    return stageI18nKey
      ? t(`project:common.stages.${stageI18nKey}`)
      : tw('values.unknownStage');
  };

  const getLocalizedProjectStatus = (status?: string) => {
    if (!status) return tw('values.unknownStatus');
    const statusKeyMap: Record<string, string> = {
      active: 'project:common.projectStatus.active',
      pending: 'project:common.projectStatus.pending',
      completed: 'project:common.projectStatus.completed',
      suspended: 'project:common.projectStatus.suspended',
      cancelled: 'project:common.projectStatus.cancelled',
      paused: 'project:common.projectStatus.paused',
      in_progress: 'project:common.projectStatus.active',
    };

    return statusKeyMap[status]
      ? t(statusKeyMap[status])
      : tw('values.unknownStatus');
  };

  const formatLocaleDate = (dateValue?: string | null) => {
    const rawDate = (dateValue || '').trim();
    if (!rawDate) return t('project:common.tbd');

    const parsedDate = new Date(rawDate);
    if (Number.isNaN(parsedDate.getTime())) return rawDate;

    return new Intl.DateTimeFormat(
      i18n.language.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US',
      {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }
    ).format(parsedDate);
  };

  // State management
  const [currentStep, setCurrentStep] = useState(
    searchParams.get('step') || 'detailed_assessment'
  );
  const [currentTask, setCurrentTask] = useState(
    searchParams.get('task') || null
  );

  // Default select first task of current stage
  useEffect(() => {
    if (!currentTask && !searchParams.get('task')) {
      const stepConfig =
        GLOBAL_WORKFLOW_CONFIG[
          currentStep as keyof typeof GLOBAL_WORKFLOW_CONFIG
        ];
      if (stepConfig?.tasks && stepConfig.tasks.length > 0) {
        const firstTask = stepConfig.tasks[0];
        setCurrentTask(firstTask.id);
        setSearchParams({ step: currentStep, task: firstTask.id });
      }
    }
  }, [currentStep, currentTask, searchParams, setSearchParams]);

  // Fetch project data
  const {
    data: projectData,
    isLoading: isProjectLoading,
    error: projectError,
    refetch: refetchProject,
  } = useGetProjectWorkflowDetailQuery(projectCode || '', {
    skip: !projectCode,
  });

  // Page state - notifications
  const [notifications] = useState([
    { id: '1', titleKey: 'notifications.assessmentPending', read: false },
    { id: '2', titleKey: 'notifications.financialReportUploaded', read: false },
    { id: '3', titleKey: 'notifications.progressUpdate', read: true },
  ]);

  // Get current step config
  const getCurrentStepConfig = () => {
    return GLOBAL_WORKFLOW_CONFIG[
      currentStep as keyof typeof GLOBAL_WORKFLOW_CONFIG
    ];
  };

  // Get current task
  const getCurrentTask = () => {
    const stepConfig = getCurrentStepConfig();
    if (!currentTask || !stepConfig?.tasks) return null;
    return stepConfig.tasks.find(task => task.id === currentTask);
  };

  // Calculate overall progress
  const calculateOverallProgress = () => {
    const steps = Object.values(GLOBAL_WORKFLOW_CONFIG);
    const totalProgress = steps.reduce((sum, step) => sum + step.progress, 0);
    return Math.round(totalProgress / steps.length);
  };

  // Get current step index in workflow
  const getCurrentStepIndex = () => {
    const stepKeys = Object.keys(GLOBAL_WORKFLOW_CONFIG);
    return stepKeys.indexOf(currentStep) + 1;
  };

  // Step change handler
  const handleStepChange = (step: string) => {
    setCurrentStep(step);
    setCurrentTask(null);
    setSearchParams({ step });
  };

  // Task change handler
  const handleTaskChange = (taskId: string) => {
    setCurrentTask(taskId);
    setSearchParams({ step: currentStep, task: taskId });
  };

  // Loading state
  if (isProjectLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div style={{ fontSize: '16px', color: '#666' }}>
          {t('project:message.loadingData')}
        </div>
      </div>
    );
  }

  // Error state
  if (projectError || !projectData?.data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div
          style={{ fontSize: '16px', color: '#ff4d4f', marginBottom: '16px' }}
        >
          {projectError
            ? t('project:message.loadFailed')
            : t('project:message.notFound')}
        </div>
        <Button type="primary" onClick={() => refetchProject()}>
          {t('project:message.reload')}
        </Button>
      </div>
    );
  }

  // Get actual project data
  const project = projectData.data.project;

  // Render project header card
  const renderProjectHeader = () => (
    <Card style={{ marginBottom: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        {/* Left side project info */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <Avatar
              size={64}
              style={{
                backgroundColor: EMERALD_COLORS.primary[500],
                color: 'white',
                marginRight: '16px',
                fontSize: '24px',
              }}
            >
              {project.project_name.substring(0, 2)}
            </Avatar>

            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <Title
                  level={2}
                  style={{
                    margin: 0,
                    marginRight: '12px',
                    color: EMERALD_COLORS.neutral[800],
                  }}
                >
                  {project.project_name}
                </Title>
                <Space>
                  <Tag color="green">
                    {getLocalizedCurrentStage(project.current_stage)}
                  </Tag>
                  <Tag
                    color={
                      project.overall_status === 'active'
                        ? 'processing'
                        : 'default'
                    }
                  >
                    {getLocalizedProjectStatus(project.overall_status)}
                  </Tag>
                  <Tag
                    color={
                      project.risk_level === 'low'
                        ? 'success'
                        : project.risk_level === 'medium'
                          ? 'warning'
                          : 'error'
                    }
                  >
                    {project.risk_level === 'low'
                      ? t('project:common.risk.low')
                      : project.risk_level === 'medium'
                        ? t('project:common.risk.medium')
                        : t('project:common.risk.high')}
                  </Tag>
                </Space>
              </div>

              <Text type="secondary" style={{ fontSize: '16px' }}>
                {project.issuer_name}
              </Text>

              <div style={{ marginTop: '8px' }}>
                <Space size="large">
                  <Text type="secondary">
                    <DollarOutlined /> {t('project:common.issuanceScale')}:{' '}
                    {project.bond_amount_display}
                  </Text>
                  <Text type="secondary">
                    {tw('term')}: {normalizeBondTerm(project.bond_term)}
                  </Text>
                  <Text type="secondary">
                    <TeamOutlined /> {tw('manager')}:{' '}
                    {project.issuer_contact_name}
                  </Text>
                  <Text type="secondary">
                    {t('project:common.exchange')}: {tw('exchangeName')}
                  </Text>
                </Space>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
              }}
            >
              <Text strong>{t('project:common.projectProgress')}</Text>
              <Text>{project.progress_percentage}%</Text>
            </div>
            <Progress
              percent={project.progress_percentage}
              strokeColor={{
                '0%': EMERALD_COLORS.primary[500],
                '100%': EMERALD_COLORS.primary[600],
              }}
              strokeWidth={8}
            />
          </div>

          {/* Project description */}
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary">{tw('projectDescription')}</Text>
          </div>

          {/* Tags */}
          <div>
            <Space size={8} wrap>
              <Tag color="green">
                {normalizeProjectType(project.green_project_type)}
              </Tag>
              <Tag>{tw('header.tags.greenBond')}</Tag>
              <Tag>{tw('header.tags.sustainable')}</Tag>
              <Tag>{tw('header.tags.carbonReduction')}</Tag>
            </Space>
          </div>
        </div>

        {/* Right side stats and date info */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            minWidth: '400px',
          }}
        >
          {/* Key dates */}
          <div style={{ marginBottom: '20px' }}>
            <Space size="large">
              <div style={{ textAlign: 'right' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('project:common.createdAt')}
                </Text>
                <br />
                <Text style={{ fontSize: '14px', fontWeight: '500' }}>
                  {formatLocaleDate(
                    project.created_at || project.created_at_display
                  )}
                </Text>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('project:common.expectedCompletion')}
                </Text>
                <br />
                <Text style={{ fontSize: '14px', fontWeight: '500' }}>
                  {formatLocaleDate(project.expected_completion)}
                </Text>
              </div>
            </Space>
          </div>

          {/* Core statistics */}
          <Row
            gutter={[16, 12]}
            style={{ marginBottom: '16px', width: '100%' }}
          >
            <Col span={12}>
              <div
                style={{
                  textAlign: 'center',
                  padding: '12px',
                  background: `linear-gradient(135deg, ${EMERALD_COLORS.primary[50]} 0%, ${EMERALD_COLORS.primary[100]} 100%)`,
                  borderRadius: '8px',
                  border: `1px solid ${EMERALD_COLORS.primary[200]}`,
                }}
              >
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: EMERALD_COLORS.primary[600],
                    marginBottom: '4px',
                  }}
                >
                  <DollarOutlined style={{ marginRight: '4px' }} />
                  {project.bond_amount_display}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('project:common.issuanceScale')}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div
                style={{
                  textAlign: 'center',
                  padding: '12px',
                  background: `linear-gradient(135deg, ${EMERALD_COLORS.semantic.success}15 0%, ${EMERALD_COLORS.semantic.success}25 100%)`,
                  borderRadius: '8px',
                  border: `1px solid ${EMERALD_COLORS.semantic.success}40`,
                }}
              >
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: EMERALD_COLORS.semantic.success,
                    marginBottom: '4px',
                  }}
                >
                  {t('project:common.tbd')}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {tw('couponRate')}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div
                style={{
                  textAlign: 'center',
                  padding: '12px',
                  background: `linear-gradient(135deg, ${EMERALD_COLORS.semantic.info}15 0%, ${EMERALD_COLORS.semantic.info}25 100%)`,
                  borderRadius: '8px',
                  border: `1px solid ${EMERALD_COLORS.semantic.info}40`,
                }}
              >
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: EMERALD_COLORS.semantic.info,
                    marginBottom: '4px',
                  }}
                >
                  <TrophyOutlined style={{ marginRight: '4px' }} />
                  {tw('scorePoints', { score: project.ai_score })}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('project:common.aiScore')}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div
                style={{
                  textAlign: 'center',
                  padding: '12px',
                  background: `linear-gradient(135deg, ${EMERALD_COLORS.neutral[100]} 0%, ${EMERALD_COLORS.neutral[200]} 100%)`,
                  borderRadius: '8px',
                  border: `1px solid ${EMERALD_COLORS.neutral[300]}`,
                }}
              >
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: EMERALD_COLORS.neutral[600],
                    marginBottom: '4px',
                  }}
                >
                  <CalendarOutlined style={{ marginRight: '4px' }} />
                  {normalizeBondTerm(project.bond_term)}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {t('project:common.bondTerm')}
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Card>
  );

  // Render compact process header
  const renderCompactProcessHeader = () => (
    <div
      style={{
        background: 'white',
        padding: '20px 24px',
        marginBottom: '24px',
        border: '1px solid #f0f0f0',
        borderRadius: '8px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* Project info and progress */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}
      >
        <div>
          <Title
            level={4}
            style={{ margin: 0, marginBottom: '4px', color: '#1f2937' }}
          >
            {project.project_name}
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {project.issuer_name} • {tw('projectCode')}: {project.project_code}
          </Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: '28px',
              fontWeight: '600',
              color: '#10b981',
              lineHeight: 1,
            }}
          >
            {calculateOverallProgress()}%
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {tw('overallProgress')}
          </Text>
        </div>
      </div>

      {/* Process step navigation */}
      <div style={{ marginBottom: '16px' }}>
        <Steps
          current={getCurrentStepIndex() - 1}
          size="small"
          style={{ marginBottom: '12px' }}
          onChange={stepIndex => {
            const stepKeys = Object.keys(GLOBAL_WORKFLOW_CONFIG);
            const selectedStepKey = stepKeys[stepIndex];
            if (selectedStepKey) {
              handleStepChange(selectedStepKey);
            }
          }}
        >
          {Object.entries(GLOBAL_WORKFLOW_CONFIG).map(([key, config]) => (
            <Step
              key={key}
              title={getStageName(config.stageKey)}
              description={tw('daysUnit', { count: config.estimatedDays })}
              status={
                config.status === 'completed'
                  ? 'finish'
                  : config.status === 'in_progress'
                    ? 'process'
                    : 'wait'
              }
            />
          ))}
        </Steps>
      </div>

      {/* Current stage info */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          background: '#f8fafc',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
        }}
      >
        <div>
          <Text strong style={{ color: '#10b981', fontSize: '14px' }}>
            {tw('currentStage')}:{' '}
            {getCurrentStepConfig()
              ? getStageName(getCurrentStepConfig().stageKey)
              : ''}{' '}
            {tw('stageOf', { current: getCurrentStepIndex(), total: 6 })}
          </Text>
          <div style={{ marginTop: '2px' }}>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {getCurrentStepConfig()
                ? getStageDescription(getCurrentStepConfig().stageKey)
                : ''}
            </Text>
          </div>
        </div>
        <Text type="secondary" style={{ fontSize: '13px' }}>
          {tw('expectedCompletionDate', {
            date: formatLocaleDate(project.expected_completion),
          })}
        </Text>
      </div>
    </div>
  );

  // Render sidebar navigation
  const renderSidebarNavigation = () => (
    <div style={{ padding: '24px' }}>
      {/* Current step tasks */}
      {getCurrentStepConfig()?.tasks &&
        getCurrentStepConfig().tasks.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <Title level={5} style={{ marginBottom: '16px', color: '#1f2937' }}>
              {tw('sidebar.currentTasks')}
            </Title>
            {getCurrentStepConfig().tasks.map((task, index) => (
              <div
                key={task.id}
                onClick={() => handleTaskChange(task.id)}
                style={{
                  padding: '12px 14px',
                  margin: '6px 0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background:
                    task.id === currentTask
                      ? `linear-gradient(135deg, ${EMERALD_COLORS.primary[50]} 0%, ${EMERALD_COLORS.primary[100]} 100%)`
                      : EMERALD_COLORS.neutral[50],
                  border:
                    task.id === currentTask
                      ? `2px solid ${EMERALD_COLORS.primary[400]}`
                      : `1px solid ${EMERALD_COLORS.neutral[200]}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow:
                    task.id === currentTask
                      ? `0 4px 12px -4px ${EMERALD_COLORS.primary[500]}40`
                      : '0 1px 4px -1px rgba(0, 0, 0, 0.05)',
                  transform:
                    task.id === currentTask ? 'translateY(-1px)' : 'none',
                }}
                onMouseEnter={e => {
                  if (task.id !== currentTask) {
                    e.currentTarget.style.background =
                      EMERALD_COLORS.neutral[100];
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow =
                      '0 4px 12px -4px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={e => {
                  if (task.id !== currentTask) {
                    e.currentTarget.style.background =
                      EMERALD_COLORS.neutral[50];
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px -2px rgba(0, 0, 0, 0.05)';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <div
                    style={{
                      marginRight: '10px',
                      padding: '6px',
                      borderRadius: '6px',
                      background:
                        task.status === 'completed'
                          ? EMERALD_COLORS.semantic.success + '15'
                          : task.status === 'in_progress'
                            ? EMERALD_COLORS.semantic.inProgress + '15'
                            : EMERALD_COLORS.semantic.pending + '15',
                    }}
                  >
                    {task.status === 'completed' ? (
                      <CheckCircleOutlined
                        style={{
                          color: EMERALD_COLORS.semantic.completed,
                          fontSize: '14px',
                        }}
                      />
                    ) : task.status === 'in_progress' ? (
                      <ClockCircleOutlined
                        style={{
                          color: EMERALD_COLORS.semantic.inProgress,
                          fontSize: '14px',
                        }}
                      />
                    ) : (
                      <ExclamationCircleOutlined
                        style={{
                          color: EMERALD_COLORS.semantic.pending,
                          fontSize: '14px',
                        }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: task.id === currentTask ? '600' : '500',
                        color:
                          task.id === currentTask
                            ? EMERALD_COLORS.primary[700]
                            : EMERALD_COLORS.neutral[700],
                        marginBottom: '6px',
                        lineHeight: '1.3',
                      }}
                    >
                      {index + 1}. {getTaskTitle(task.id)}
                    </div>
                    <Progress
                      percent={task.progress}
                      size="small"
                      strokeColor={
                        task.status === 'completed'
                          ? EMERALD_COLORS.semantic.completed
                          : task.status === 'in_progress'
                            ? EMERALD_COLORS.semantic.inProgress
                            : EMERALD_COLORS.semantic.pending
                      }
                      trailColor={EMERALD_COLORS.neutral[200]}
                      showInfo={false}
                      strokeWidth={4}
                    />
                  </div>
                </div>
                <Badge
                  count={`${task.progress}%`}
                  style={{
                    backgroundColor:
                      task.status === 'completed'
                        ? EMERALD_COLORS.semantic.completed
                        : task.status === 'in_progress'
                          ? EMERALD_COLORS.semantic.inProgress
                          : EMERALD_COLORS.semantic.pending,
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '0 6px',
                    borderRadius: '10px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  }}
                />
              </div>
            ))}
          </div>
        )}

      {/* Quick actions */}
      <div>
        <Title
          level={5}
          style={{
            marginBottom: '16px',
            color: EMERALD_COLORS.neutral[700],
            fontSize: '16px',
            fontWeight: '600',
          }}
        >
          {tw('sidebar.quickActions')}
        </Title>
        <Space direction="vertical" style={{ width: '100%', gap: '8px' }}>
          <Button
            block
            icon={<InfoCircleOutlined />}
            style={{
              height: '44px',
              borderRadius: '10px',
              border: `1px solid ${EMERALD_COLORS.primary[200]}`,
              background: `linear-gradient(135deg, ${EMERALD_COLORS.primary[50]} 0%, ${EMERALD_COLORS.primary[100]} 100%)`,
              color: EMERALD_COLORS.primary[700],
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(16, 185, 129, 0.1)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow =
                '0 4px 12px rgba(16, 185, 129, 0.15)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow =
                '0 2px 4px rgba(16, 185, 129, 0.1)';
            }}
          >
            {tw('sidebar.progressOverview')}
          </Button>
          <Button
            block
            icon={<FileTextOutlined />}
            style={{
              height: '44px',
              borderRadius: '10px',
              border: `1px solid ${EMERALD_COLORS.neutral[200]}`,
              background: EMERALD_COLORS.neutral[50],
              color: EMERALD_COLORS.neutral[600],
              fontWeight: '500',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = EMERALD_COLORS.neutral[100];
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = EMERALD_COLORS.neutral[50];
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {tw('sidebar.generateReport')}
          </Button>
          <Button
            block
            icon={<TeamOutlined />}
            style={{
              height: '44px',
              borderRadius: '10px',
              border: `1px solid ${EMERALD_COLORS.neutral[200]}`,
              background: EMERALD_COLORS.neutral[50],
              color: EMERALD_COLORS.neutral[600],
              fontWeight: '500',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = EMERALD_COLORS.neutral[100];
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = EMERALD_COLORS.neutral[50];
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {tw('sidebar.getHelp')}
          </Button>
        </Space>
      </div>
    </div>
  );

  // Render role-based alert
  const renderRoleBasedAlert = () => {
    if (userRole === 'issuer') {
      const currentTaskData = getCurrentTask();
      return (
        <Alert
          message={tw('alert.yourTasks')}
          description={
            <div>
              <div>
                {tw('alert.currentTodo')} {tw('alert.complete')}
                {getTaskTitle(currentTaskData?.id || '') ||
                  tw('alert.selectTask')}
              </div>
              <div>
                {tw('alert.estimatedTime')}{' '}
                {tw('minutesUnit', {
                  count: currentTaskData?.estimatedMinutes || 0,
                })}
              </div>
              <div>{tw('alert.afterCompletion')}</div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      );
    }

    return null;
  };

  // Render bottom action bar
  const renderBottomActionBar = () => {
    const currentTaskData = getCurrentTask();
    const stepConfig = getCurrentStepConfig();

    return (
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'white',
          borderTop: '1px solid #e5e7eb',
          padding: '12px 24px',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        {/* Left: current status info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {tw('status.autoSaved')}
            </Text>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {tw('status.lastSaved')} {new Date().toLocaleTimeString()}
            </div>
          </div>

          {currentTaskData && (
            <>
              <Divider type="vertical" style={{ height: '32px' }} />
              <div>
                <Text strong style={{ fontSize: '14px' }}>
                  {getTaskTitle(currentTaskData.id)}
                </Text>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {tw('status.progress')} {currentTaskData.progress}% •{' '}
                  {tw('status.estimatedRemaining')}{' '}
                  {tw('minutesUnit', {
                    count: Math.ceil(
                      ((100 - currentTaskData.progress) *
                        currentTaskData.estimatedMinutes) /
                        100
                    ),
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right: action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Task navigation buttons */}
          {stepConfig?.tasks && stepConfig.tasks.length > 1 && (
            <>
              <Button
                icon={<ArrowLeftOutlined />}
                disabled={
                  !currentTask ||
                  stepConfig.tasks.findIndex(t => t.id === currentTask) === 0
                }
                onClick={() => {
                  const currentIndex = stepConfig.tasks.findIndex(
                    t => t.id === currentTask
                  );
                  if (currentIndex > 0) {
                    handleTaskChange(stepConfig.tasks[currentIndex - 1].id);
                  }
                }}
              >
                {tw('actions.prevTask')}
              </Button>
              <Button
                icon={<ArrowRightOutlined />}
                disabled={
                  !currentTask ||
                  stepConfig.tasks.findIndex(t => t.id === currentTask) ===
                    stepConfig.tasks.length - 1
                }
                onClick={() => {
                  const currentIndex = stepConfig.tasks.findIndex(
                    t => t.id === currentTask
                  );
                  if (currentIndex < stepConfig.tasks.length - 1) {
                    handleTaskChange(stepConfig.tasks[currentIndex + 1].id);
                  }
                }}
              >
                {tw('actions.nextTask')}
              </Button>
            </>
          )}

          {/* Role-specific action buttons */}
          {userRole === 'issuer' && currentTask && (
            <>
              <Button
                icon={<SaveOutlined />}
                onClick={() => {
                  message.success(tw('messages.dataSaved'));
                }}
              >
                {tw('actions.saveDraft')}
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => {
                  message.success(tw('messages.taskSubmitted'));
                }}
              >
                {tw('actions.submitTask')}
              </Button>
            </>
          )}

          {(userRole === 'exchange' || userRole === 'admin') && currentTask && (
            <>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  message.success(tw('messages.taskApproved'));
                }}
              >
                {tw('actions.approveTask')}
              </Button>
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  message.warning(tw('messages.taskRejected'));
                }}
              >
                {tw('actions.rejectTask')}
              </Button>
              <Button
                icon={<ExclamationCircleOutlined />}
                onClick={() => {
                  message.info(tw('messages.supplementRequested'));
                }}
              >
                {tw('actions.requestSupplement')}
              </Button>
            </>
          )}

          {/* Step-level action */}
          {stepConfig?.progress === 100 &&
            (userRole === 'exchange' || userRole === 'admin') && (
              <Button
                type="primary"
                size="large"
                icon={<RightOutlined />}
                onClick={() => {
                  message.success(tw('messages.stepCompleted'));
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                }}
              >
                {tw('actions.completeStep')}
              </Button>
            )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          background: '#f5f5f5',
          paddingBottom: '80px',
        }}
      >
        {/* Breadcrumb navigation */}
        <div
          style={{
            background: 'white',
            padding: '12px 24px',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: '64px',
            zIndex: 100,
            marginTop: '16px',
          }}
        >
          <Breadcrumb>
            <Breadcrumb.Item>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/admin/project-management')}
                style={{ padding: 0, color: '#666' }}
              >
                {tw('breadcrumb.projectManagement')}
              </Button>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {tw('breadcrumb.issuanceProcess')}
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span
                style={{
                  color: EMERALD_COLORS.primary[600],
                  fontWeight: '500',
                }}
              >
                {tw('breadcrumb.detailedAssessment')}
              </span>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>

        {/* Page content */}
        <div style={{ padding: '40px 24px 24px 24px' }}>
          {/* Project header card */}
          {renderProjectHeader()}

          <Layout style={{ background: 'transparent' }}>
            {/* Global process navigation header */}
            {renderCompactProcessHeader()}

            <Layout style={{ background: 'transparent' }}>
              {/* Left navigation */}
              <Sider
                width={320}
                style={{
                  background: '#f8fafc',
                  borderRight: '1px solid #e5e7eb',
                  overflow: 'auto',
                  height: 'calc(100vh - 400px)',
                  position: 'sticky',
                  top: 0,
                  borderRadius: '8px',
                }}
              >
                {renderSidebarNavigation()}
              </Sider>

              {/* Main content area */}
              <Content
                style={{
                  padding: '0 24px',
                  background: 'transparent',
                  minHeight: 'calc(100vh - 400px)',
                }}
              >
                {/* Role-based alert */}
                {renderRoleBasedAlert()}

                {/* Task content */}
                <TaskContent
                  step={currentStep}
                  task={currentTask}
                  projectCode={projectCode!}
                  userRole={userRole}
                  hideActionButtons={true}
                />
              </Content>
            </Layout>
          </Layout>
        </div>
      </div>

      {/* Use React Portal to render action bar to document.body */}
      {createPortal(renderBottomActionBar(), document.body)}
    </>
  );
};

export default ProjectDetail;
